import { DropdownMenu, Flex, Text, Tooltip } from '@radix-ui/themes';
import { ChevronDown, CircleQuestionMark, Sparkles, Menu } from 'lucide-react';
import React, { use } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useLocation } from 'react-router';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import LocaleContext from '../hooks/LocaleContext';
import { track } from '../../analytics/track';
import { AnalyticsEvents } from '../../analytics/events';
import PinnedJobs from './PinnedJobs';
import Searchbar from './Searchbar';
import User from './User';
import LanguageGlobe from './LanguageGlobe';

const HelpCenter = () => {
  const { t } = useTranslation();
  return (
    <Tooltip content={<Text size="3">{t('nav.helpCenter.tooltip')}</Text>}>
      <a
        href="https://hubert9080.zendesk.com/"
        target="_blank"
        className="navbar-item flex text-white"
        rel="noreferrer"
        aria-label={t('nav.helpCenter.tooltip')}
        onClick={() => track(AnalyticsEvents.HELP_CENTER_OPENED)}
      >
        <CircleQuestionMark className="my-auto" size={20} />
      </a>
    </Tooltip>
  );
};

const MainLinks = ({ isMobile = false }: { isMobile?: boolean }) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const voiceOutreachEnabled = useFeatureFlagEnabled('voice-outreach-recruiter-app');
  const tempateAgentEnabled = useFeatureFlagEnabled('enable-template-agent-recruiters');
  const isOutreachActive = pathname.startsWith('/outreach');

  const navItems = [
    { to: '/jobs', label: t('nav.links.jobs') },
    { to: '/templates', label: t('nav.links.templates') },
    { to: '/questions', label: t('nav.links.questions') },
    ...(voiceOutreachEnabled
      ? [
          {
            label: t('nav.links.outreach'),
            isActive: isOutreachActive,
            mobileSeparatorBefore: true,
            children: [
              { to: '/outreach/campaigns', label: t('nav.links.outreachCampaigns') },
              { to: '/outreach/templates', label: t('nav.links.outreachTemplates') },
            ],
          },
        ]
      : []),
    ...(tempateAgentEnabled
      ? [
          {
            to: '/agent-chat',
            label: t('nav.links.agent'),
            icon: <Sparkles size={16} className="mr-2" />,
            mobileSeparatorBefore: true,
          },
        ]
      : []),
  ];

  return (
    <>
      {navItems.map((item, index) => {
        if (item.children) {
          if (isMobile) {
            return (
              <React.Fragment key={index}>
                {item.mobileSeparatorBefore && <DropdownMenu.Separator />}
                <DropdownMenu.Label>
                  <Text color="gray" highContrast weight="bold">
                    {item.label}
                  </Text>
                </DropdownMenu.Label>
                {item.children.map((child) => (
                  <DropdownMenu.Item key={child.to} asChild>
                    <NavLink to={child.to} className="dropdown-link">
                      {child.label}
                    </NavLink>
                  </DropdownMenu.Item>
                ))}
              </React.Fragment>
            );
          } else {
            return (
              <DropdownMenu.Root key={index}>
                <DropdownMenu.Trigger>
                  <button
                    className={`navbar-item flex items-center bg-transparent border-0 text-white cursor-pointer ${item.isActive ? 'active' : ''}`}
                  >
                    {item.label} <ChevronDown size={14} className="ml-1" />
                  </button>
                </DropdownMenu.Trigger>
                <DropdownMenu.Content variant="soft" color="gray">
                  {item.children.map((child) => (
                    <DropdownMenu.Item key={child.to} asChild>
                      <NavLink to={child.to} className="dropdown-link">
                        {child.label}
                      </NavLink>
                    </DropdownMenu.Item>
                  ))}
                </DropdownMenu.Content>
              </DropdownMenu.Root>
            );
          }
        }

        if (isMobile) {
          return (
            <React.Fragment key={index}>
              {item.mobileSeparatorBefore && <DropdownMenu.Separator />}
              <DropdownMenu.Item asChild>
                <NavLink to={item.to!} className="dropdown-link flex items-center">
                  <span className="flex flex-row items-center">
                    {item.icon}
                    {item.label}
                  </span>
                </NavLink>
              </DropdownMenu.Item>
            </React.Fragment>
          );
        } else {
          return (
            <NavLink
              key={index}
              className={({ isActive }) => 'navbar-item flex items-center' + (isActive ? ' active' : '')}
              end
              to={item.to!}
            >
              <span className="flex flex-row items-center">
                {item.icon}
                {item.label}
              </span>
            </NavLink>
          );
        }
      })}
    </>
  );
};

const NavbarTop = () => {
  const { t } = useTranslation();
  const { userSettings } = use(LocaleContext);

  return (
    <nav className="navbar-top sticky top-0 w-full" aria-label="Main Navigation">
      <Flex align="center" justify="between" className="navbar-container mx-auto h-full px-6">
        <Flex align="center" gap="4" className="flex-1">
          <NavLink className={({ isActive }) => (isActive ? 'active' : '')} end to="/" aria-label={t('nav.logo.alt')}>
            <img src="/logo/LogoNaked@0.5x.png" className="navbar-logo" alt={t('nav.logo.alt')} />
          </NavLink>

          <div className="hidden lg:flex items-center gap-3 flex-1">
            <MainLinks />
          </div>
        </Flex>

        <Flex align="center" className="navbar-actions gap-3">
          <Searchbar />

          <div className="hidden lg:flex items-center gap-3">
            <PinnedJobs />
            <LanguageGlobe />
            <HelpCenter />
            {userSettings.company?.name}
            <User />
          </div>

          <div className="lg:hidden flex items-center">
            <DropdownMenu.Root>
              <DropdownMenu.Trigger>
                <button
                  className="navbar-item bg-transparent border-0 text-white cursor-pointer p-1"
                  aria-label={t('nav.logo.alt')} // Reusing logo alt as a generic label for the menu if needed
                >
                  <Menu size={28} />
                </button>
              </DropdownMenu.Trigger>
              <DropdownMenu.Content variant="soft" color="gray" align="end">
                <MainLinks isMobile />

                <DropdownMenu.Separator />
                <DropdownMenu.Label>
                  <Text color="gray" highContrast weight="bold">
                    {t('nav.accountAndSupport')}
                  </Text>
                </DropdownMenu.Label>
                <PinnedJobs asSubmenu />
                <LanguageGlobe asSubmenu />
                <DropdownMenu.Item className="mx-0" asChild>
                  <a
                    href="https://hubert9080.zendesk.com/"
                    target="_blank"
                    rel="noreferrer"
                    className="dropdown-link flex flex-row items-center mx-0"
                  >
                    <CircleQuestionMark size={18} className="mr-2" /> {t('nav.helpCenter.tooltip')}
                  </a>
                </DropdownMenu.Item>
                <User asSubmenu />
              </DropdownMenu.Content>
            </DropdownMenu.Root>
          </div>
        </Flex>
      </Flex>
    </nav>
  );
};

export default NavbarTop;
